import React, { useCallback } from 'react';
import { usePlaidLink } from 'react-plaid-link';
import { enqueueSnackbar } from 'notistack';
import { Button, CircularProgress } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import usePrequal from '../../resources/prequal/usePrequal';
import useCreateBankAccount from '../../resources/bankAccounts/create';
import useBankAccounts from '../../resources/bankAccounts/read';
import useApplication from '../../resources/applications/read';
import usePlaidLinkToken from './usePlaidLinkToken';

const PlaidLink = () => {
  const { data: prequal } = usePrequal();
  const { data: token } = usePlaidLinkToken({ user_id: prequal.capsId });
  const { data: bankAccounts } = useBankAccounts();
  const {
    mutate: mutateApplication,
    isValidating: isValidatingApplication,
  } = useApplication();
  const {
    trigger: createBankAccount,
    isMutating: isCreatingBankAccount,
  } = useCreateBankAccount();

  const showErrorSnackbar = () => {
    enqueueSnackbar('There was an error with your request. Please refresh and try again.', { variant: 'error' });
  };

  const onSuccess = useCallback(async (publicToken, metadata) => {
    try {
      await createBankAccount({
        type: 'PLAID',
        public_token: publicToken,
        metadata,
      });
      await mutateApplication(); // needed for AutoPayTermsAndConditions
      enqueueSnackbar('Your bank account was added.', { variant: 'success' });
    } catch {
      showErrorSnackbar();
    }
  }, [createBankAccount, mutateApplication]);

  const onExit = useCallback((error) => {
    if (error) {
      showErrorSnackbar();
    }
  }, []);

  const { open, ready } = usePlaidLink({
    token: token.link_token,
    onSuccess,
    onExit,
    clientName: 'DigniFi',
    product: ['auth', 'identity'],
    env: process.env.PLAID_ENV,
  });

  const loading = !ready
    || isCreatingBankAccount
    || isValidatingApplication;

  return (
    <Button
      fullWidth
      variant={bankAccounts.length > 0 ? 'outlined' : 'contained'}
      disabled={loading}
      startIcon={loading ? <CircularProgress size={18} color="info" /> : <AddIcon size={18} />}
      onClick={() => open()}
    >
      Add a New Bank
    </Button>
  );
};

export default PlaidLink;
